import { KeyRound, Leaf, ShieldCheck } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { auth } from "@/api/endpoints";
import { isTotpChallenge } from "@/api/types";
import { Button } from "@/components/ui/button";
import { FormError, Spinner } from "@/components/ui/feedback";
import { Input, Label } from "@/components/ui/input";
import { useAuth } from "@/auth/AuthProvider";

type Step =
  | { kind: "password" }
  | { kind: "totp"; challengeToken: string; enrolled: boolean };

export default function Login() {
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const [step, setStep] = React.useState<Step>({ kind: "password" });
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await auth.login(email.trim(), password);
      if (isTotpChallenge(result)) {
        setStep({
          kind: "totp",
          challengeToken: result.challenge_token,
          enrolled: result.totp_enrolled,
        });
      } else {
        await signIn(result.access_token, result.refresh_token);
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not reach the server.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitTotp(e: React.FormEvent) {
    e.preventDefault();
    if (step.kind !== "totp") return;
    setError(null);
    setBusy(true);
    try {
      const tokens = await auth.verifyTotp(step.challengeToken, code.trim());
      await signIn(tokens.access_token, tokens.refresh_token);
      navigate("/", { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not reach the server.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <div className="flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Leaf className="size-6" />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Kheti24 Console</h1>
            <p className="mt-1 text-xs text-muted-foreground">
              Back-office access for support and operations
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          {step.kind === "password" ? (
            <form onSubmit={submitPassword} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Work email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@kheti24.in"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <FormError message={error} />
              <Button type="submit" className="w-full" disabled={busy}>
                {busy && <Spinner />}
                Sign in
              </Button>
            </form>
          ) : (
            <form onSubmit={submitTotp} className="space-y-4">
              <div className="flex items-start gap-2.5 rounded-md bg-secondary p-3">
                <ShieldCheck className="mt-px size-4 shrink-0 text-secondary-foreground" />
                <p className="text-xs text-secondary-foreground">
                  {step.enrolled
                    ? "Enter the 6-digit code from your authenticator app."
                    : "Two-factor is required but not yet set up on this account. Ask an administrator to complete enrolment."}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="code">Authentication code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  disabled={!step.enrolled}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="tabular text-center text-lg tracking-[0.3em]"
                  placeholder="000000"
                />
              </div>
              <FormError message={error} />
              <Button
                type="submit"
                className="w-full"
                disabled={busy || !step.enrolled || code.length !== 6}
              >
                {busy ? <Spinner /> : <KeyRound />}
                Verify
              </Button>
              <button
                type="button"
                onClick={() => {
                  setStep({ kind: "password" });
                  setCode("");
                  setError(null);
                }}
                className="w-full text-xs text-muted-foreground underline underline-offset-4"
              >
                Back to sign in
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-[11px] text-muted-foreground">
          Staff accounts only. Farmers and service providers use the mobile app.
        </p>
      </div>
    </div>
  );
}
